import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

type SetSearchParams = ReturnType<typeof useSearchParams>[1];

/**
 * Mirrors a set of filter values into the URL so a filtered list survives a
 * refresh and can be shared or bookmarked. `params` is the full key -> current
 * value map to sync (e.g. search/status/date-range plus any page-specific
 * filter); an empty value removes that key instead of setting it.
 */
export function useFilterUrlSync(
  setSearchParams: SetSearchParams,
  params: Record<string, string>
): void {
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        Object.entries(params).forEach(([key, value]) => {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        });
        return next;
      },
      { replace: true }
    );
    // params is re-derived from paramsKey every render; only paramsKey
    // actually needs to trigger the sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, setSearchParams]);
}
